import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import Avatar from "../components/Avatar";
import ChatMessageList from "../components/ChatMessageList";
import MessageComposer from "../components/MessageComposer";
import { useTranslation } from "../i18n/LanguageContext";
import { useChatNotifications } from "../contexts/ChatNotificationsContext";
import { useChatThread } from "../hooks/useChatThread";
import { useAutoScroll } from "../hooks/useAutoScroll";

const ERROR_MESSAGES = {
  loadError: (t) => t("chat.loadError", "Error loading chat. Please try again."),
  sendError: (t) => t("chat.sendError", "Failed to send message."),
};

function ChatPage() {
  const { t } = useTranslation();
  const { buddyId } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [buddyName, setBuddyName] = useState("");
  const [buddyPhotoUrl, setBuddyPhotoUrl] = useState("");
  const [buddyAvatarConfig, setBuddyAvatarConfig] = useState(null);
  const chatNotifications = useChatNotifications();

  // Mark this buddy's chat as the active/visible one for as long as this
  // page is mounted, so incoming messages here never trigger a redundant
  // toast and are immediately reflected as read (see ChatNotificationsContext).
  useEffect(() => {
    chatNotifications?.setActiveChatBuddy(buddyId);
    if (buddyId) chatNotifications?.markChatRead(buddyId);
    return () => chatNotifications?.setActiveChatBuddy(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buddyId]);

  // Track current user
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsub();
  }, []);

  // Buddy display info (name/photo) — separate from the message thread.
  useEffect(() => {
    if (!buddyId) return;
    let cancelled = false;
    (async () => {
      try {
        const buddyDocSnap = await getDoc(doc(db, "users", buddyId));
        if (!cancelled && buddyDocSnap.exists()) {
          const buddyData = buddyDocSnap.data();
          setBuddyName(buddyData.username || "");
          setBuddyPhotoUrl(buddyData.photoUrl || "");
          setBuddyAvatarConfig(buddyData.avatarConfig || null);
        }
      } catch (err) {
        console.error("Failed to load buddy profile:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buddyId]);

  const { messages, loading, error, sending, sendMessage } = useChatThread(
    currentUser?.uid,
    buddyId
  );
  const { containerRef, bottomRef, hasNewBelow, scrollToBottom } = useAutoScroll(
    messages,
    buddyId
  );

  // Mark newly-arrived messages read immediately while this page is open
  // (in addition to the context's own active-chat handling).
  useEffect(() => {
    if (messages.length > 0) chatNotifications?.markChatRead(buddyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, buddyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-white text-lg font-semibold">{t("chat.loading", "Loading chat...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-var(--navbar-h,64px))] w-full max-w-full flex-col overflow-x-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 p-3 sm:p-6">
      <div className="mx-auto flex w-full max-w-2xl min-h-0 flex-1 flex-col">
        {/* Header */}
        <div className="mb-3 flex shrink-0 items-center justify-between gap-2 sm:mb-4">
          <button
            onClick={() => navigate("/buddies")}
            className="shrink-0 text-base font-bold text-white transition hover:text-blue-400 sm:text-lg"
          >
            ← {t("chat.back", "Back")}
          </button>
          <h2 className="flex min-w-0 items-center gap-2 text-lg font-bold text-white drop-shadow sm:gap-3 sm:text-3xl">
            <Avatar src={buddyPhotoUrl} avatarConfig={buddyAvatarConfig} size={36} alt={buddyName || t("chat.title", "Chat")} />
            <span className="truncate">
              {buddyName || t("chat.title", "Chat")}
            </span>
          </h2>
          <div className="w-10 sm:w-16 shrink-0"></div>
        </div>

        {error && (
          <div className="mb-3 shrink-0 rounded-lg border border-red-700 bg-red-900/50 p-3 text-sm font-medium text-red-200">
            {ERROR_MESSAGES[error]?.(t) || ERROR_MESSAGES.loadError(t)}
          </div>
        )}

        {/* Messages Container — the only part of this page that scrolls;
            min-h-0 is required so a flex child can actually shrink below its
            content size and become scrollable instead of overflowing the page. */}
        <div className="relative mb-3 min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/90 p-4 shadow-xl backdrop-blur-sm sm:mb-4 sm:p-6">
          <ChatMessageList
            messages={messages}
            currentUid={currentUser?.uid}
            containerRef={containerRef}
            bottomRef={bottomRef}
            emptyLabel={t("chat.empty", "No messages yet. Start the conversation! 👋")}
            className="h-full overflow-y-auto"
          />
          {hasNewBelow && (
            <button
              type="button"
              onClick={() => scrollToBottom("smooth")}
              className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-lg transition hover:bg-blue-500"
            >
              ↓ {t("chat.newMessagesIndicator", "New messages")}
            </button>
          )}
        </div>

        {/* Input Area — always shrink-0 so it never scrolls out of view;
            bottom safe-area padding keeps it clear of mobile browser chrome. */}
        <div className="shrink-0" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <MessageComposer onSend={sendMessage} sending={sending} disabled={!currentUser} />
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
